<template>
  <AsteonlineCard v-if="!showIfBookmarked || (showIfBookmarked && isBookmarked)">
    <CardHead>
      {{ lot.code }}

      <LotBadge
        :lot="lot"
        :my-lot="myLot"
        :is-outbid="isOutbid"
      />

      <div class="float-right">
        <BookmarkIcon
          :lot="lot"
          :is-bookmarked="isBookmarked"
          @added="isBookmarked = true"
          @removed="isBookmarked = false"
        />
      </div>
    </CardHead>

    <CardBody>
      <div class="flex-grow">
        <div class="flex">
          <div class="flex-grow">
            <div>
              {{ $t("product.product") }} <span class="font-bold">
                {{ productMap[lot.productUuid].code }} - {{ JSON.parse(productMap[lot.productUuid].multilangName)[$i18n.locale] }}
              </span>
            </div>
            <div>
              {{ $t("auction.waste_code") }} <span
                class="font-bold"
                :style="{ color: stc(lot.wasteCode) }"
              >
                {{ lot.wasteCode }}
              </span>
            </div>
            <div>
              {{ $t("auction.expiration") }} <span class="font-bold">{{ dateUtils.format(lot.endAt) }}</span>
            </div>
          </div>
          <div>
            <WarehouseButton @click.prevent="warehousesModal = true" />
            <AttributesButton @click.prevent="attributesModal = true" />
          </div>
        </div>
        <div class="flex mt-2">
          <div class="w-full md:w-3/5 grid grid-cols-2 md:grid-cols-4">
            <div>
              <div class="bg-white shadow">
                <div class="text-center text-xs bg-corepla-bg-dark text-white py-1">
                  {{ $t("common.quantity") }}
                </div>
                <div class="font-semibold text-center py-2 bg-corepla-bg-light h-[40px] md:h-[50px]">
                  {{ lot.quantity }} t
                </div>
              </div>
            </div>
            <div>
              <div class="bg-white shadow">
                <div class="text-center text-xs bg-corepla-bg-dark text-white py-1">
                  {{ $t("auction.effective_price") }}
                </div>
                <div class="font-semibold text-center py-2 bg-corepla-bg-light h-[40px] md:h-[50px]">
                  {{ lot.currentPrice }} €/t
                </div>
              </div>
            </div>
            <div>
              <div class="bg-white shadow">
                <div class="text-center text-xs bg-corepla-bg-dark text-white py-1">
                  {{ $t("auction.bid_count") }}
                </div>
                <div class="font-semibold text-center py-2 bg-corepla-bg-light h-[40px] md:h-[50px]">
                  {{ lot.bidCount }}
                </div>
              </div>
            </div>
            <div v-if="auction.auctionStatus == 'OPEN' || auction.auctionStatus == 'PUBLISHED'">
              <div class="bg-white shadow">
                <div class="text-center text-xs bg-corepla-bg-dark text-white py-1">
                  {{ $t("auction.expiration") }}
                </div>
                <div class="font-semibold text-center py-2 bg-corepla-bg-light h-[40px] md:h-[50px]">
                  <CircleCountdown
                    v-if="auction.auctionStatus == 'OPEN'"
                    :start="auction.startAt"
                    :end="lot.endAt"
                    :only-text="true"
                  />
                  <span v-else>
                    {{ dateUtils.format(lot.endAt) }}
                  </span>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="bg-white shadow">
                <div class="text-center text-xs bg-corepla-bg-dark text-white py-1">
                  Quantità assegnata
                </div>
                <div class="font-semibold text-center py-2 bg-corepla-bg-light h-[40px] md:h-[50px]">
                  {{ lot.assignedQuantity }} t
                </div>
              </div>
            </div>
          </div>

          <div class="hidden md:block md:w-2/5">
            <div class="px-2 w-full max-h-[100px]">
              <LotBidHistoryLine
                v-if="lot.bids.length > 1"
                :bids="lot.bids"
                :lot="lot"
                :my-lot="myLot"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        class="mt-2 "
      >
        <div
          v-if="auction.auctionStatus == 'OPEN'"
          class="w-1/2 md:w-1/3 lg:w-1/4 mx-auto"
        >
          <BidButton
            v-if="authStore.canBid && myLot.canBid && !lot.closed"
            :auction="auction"
            :lot="lot"
            :my-lot="myLot"
            @click.prevent="biddingLot = lot"
          />
        </div>
      </div>
    </CardBody>
  </AsteonlineCard>

  <BidModal
    v-if="authStore.canBid && biddingLot"
    :auction="auction"
    :lot="biddingLot"
    :my-lot="myLot"
    @close="biddingLot = null"
    @update-auction="$emit('update-auction', $event)"
  />

  <WarehousesModal
    v-if="warehousesModal"
    :lot="lot"
    @close="warehousesModal = false"
  />

  <AttributesModal
    v-if="attributesModal"
    :lot="lot"
    @close="attributesModal = false"
  />
</template>

<script setup>
import stc from 'string-to-color';
import { ref } from 'vue'

import BidModal from '@/components/auction/BidModal.vue';
import AsteonlineCard from '@/components/card/AsteonlineCard.vue';
import CardBody from '@/components/card/CardBody.vue';
import CardHead from '@/components/card/CardHead.vue';
import LotBidHistoryLine from '@/components/charts/LotBidHistoryLine.vue';
import CircleCountdown from '@/components/CircleCountdown.vue';
import AttributesButton from '@/components/lot/AttributesButton.vue';
import AttributesModal from '@/components/lot/AttributesModal.vue';
import BidButton from '@/components/lot/BidButton.vue';
import BookmarkIcon from '@/components/lot/BookmarkIcon.vue';
import LotBadge from '@/components/lot/LotBadge.vue';
import WarehouseButton from '@/components/lot/WarehouseButton.vue';
import WarehousesModal from '@/components/lot/WarehousesModal.vue';
import dateUtils from '@/services/date-utils'
import lotBookmark from '@/services/lot-bookmarks';
import { useAuthStore } from '@/stores/auth';

const emit = defineEmits(['update-auction'])

const props = defineProps({
    auction: {
        type: Object,
        required: true
    },
    lot: {
        type: Object,
        required: true
    },
    myLot: {
        type: Object,
        required: false,
        default: null
    },
    productMap: {
        type: Object,
        required: true
    },
    showIfBookmarked: {
        type: Boolean,
        required: true
    },
    isOutbid: {
        type: Boolean,
        required: false
    }
})

const authStore = useAuthStore();

const warehousesModal = ref(false)
const attributesModal = ref(false)
const isBookmarked = ref(lotBookmark.isBookmarked(props.lot.uuid));

const biddingLot = ref(null)

</script>